export type PromptSyntaxKind = "plain" | "field" | "reference" | "timestamp" | "camera" | "continuity" | "constraint" | "sound";

export type PromptSyntaxToken = {
  kind: PromptSyntaxKind;
  text: string;
};

export type PromptSectionLink = {
  field: string;
  label: string;
  index: number;
};

export type BilingualPromptSection = {
  key: "reference" | "direction" | "sound" | "music";
  label: string;
  field: string | null;
  english: string;
  korean: string;
  start: number;
  end: number;
};

export type BilingualSentencePair = {
  english: string;
  korean: string;
  index: number;
};

const BILINGUAL_FIELDS = [
  { key: "direction" as const, label: "장면 · 동작 · 카메라", field: "integrated_multimodal_description" },
  { key: "sound" as const, label: "현장음 · 효과음", field: "overall_soundscape" },
  { key: "music" as const, label: "배경 음악", field: "non_diegetic_music" },
];

const FIELD_LABELS = [
  ["subject_definitions:", "대상 정의"],
  ["summary:", "요약"],
  ["retention_analysis:", "보존 범위"],
  ["detailed_description:", "장면 연출"],
  ["integrated_multimodal_description:", "장면·동작"],
  ["overall_soundscape:", "현장음"],
  ["non_diegetic_music:", "배경 음악"],
] as const;

const RULES: Array<{ kind: Exclude<PromptSyntaxKind, "plain">; pattern: RegExp }> = [
  {
    kind: "field",
    pattern: /\b(?:subject_definitions|summary|retention_analysis|detailed_description|integrated_multimodal_description|overall_soundscape|non_diegetic_music):/gi,
  },
  {
    kind: "reference",
    pattern: /<(?:Picture|Video|Audio|Subject) \d+>|\[Shot \d+\]|\b(?:KEEP|attribute_transfer|fully_preserved)\b/gi,
  },
  {
    kind: "timestamp",
    pattern: /\b(?:From\s+)?\d+(?:\.\d+)?(?:\s+(?:to|until)\s+\d+(?:\.\d+)?)?\s+seconds?\b|\bAt\s+\d{2}:\d{2}(?:\.\d+)?\b/gi,
  },
  {
    kind: "camera",
    pattern: /\b(?:Static Shot|Tracking Shot|Push In|Pull Out|Zoom In|Zoom Out|Pan Left|Pan Right|Truck Left|Truck Right|Pedestal Up|Pedestal Down|handheld|camera recoils?|operator sway)\b/gi,
  },
  {
    kind: "continuity",
    pattern: /\b(?:preserve|preserves|maintain|maintains|remains?|same|exact|continue(?:s|d)? from|stay(?:s)? fixed|settles?)\b/gi,
  },
  {
    kind: "constraint",
    pattern: /\b(?:no|never|without|must not|do not|does not|cannot)\b/gi,
  },
  {
    kind: "sound",
    pattern: /\b(?:synchronized|soundscape|room tone|footsteps?|breathing|whoosh|impact|contact sound|rustle|dialogue|music|audio)\b/gi,
  },
];

type MatchCandidate = { kind: Exclude<PromptSyntaxKind, "plain">; index: number; text: string; priority: number };

export function tokenizePromptSyntax(prompt: string): PromptSyntaxToken[] {
  if (!prompt) return [];
  const tokens: PromptSyntaxToken[] = [];
  let cursor = 0;
  while (cursor < prompt.length) {
    let next: MatchCandidate | null = null;
    RULES.forEach((rule, priority) => {
      rule.pattern.lastIndex = cursor;
      const match = rule.pattern.exec(prompt);
      if (!match) return;
      const candidate = { kind: rule.kind, index: match.index, text: match[0], priority };
      if (!next || candidate.index < next.index || (candidate.index === next.index && candidate.priority < next.priority)) {
        next = candidate;
      }
    });
    if (!next) {
      tokens.push({ kind: "plain", text: prompt.slice(cursor) });
      break;
    }
    const match = next as MatchCandidate;
    if (match.index > cursor) tokens.push({ kind: "plain", text: prompt.slice(cursor, match.index) });
    tokens.push({ kind: match.kind, text: match.text });
    cursor = match.index + match.text.length;
  }
  return tokens;
}

export function promptSectionLinks(prompt: string): PromptSectionLink[] {
  return FIELD_LABELS.map(([field, label]) => ({
    field,
    label,
    index: prompt.toLocaleLowerCase().indexOf(field.toLocaleLowerCase()),
  })).filter((item) => item.index >= 0).sort((a, b) => a.index - b.index);
}

function fieldMarkers(value: string) {
  const lower = value.toLocaleLowerCase();
  return BILINGUAL_FIELDS.map((definition) => ({
    ...definition,
    index: lower.indexOf(`${definition.field}:`),
  })).filter((definition) => definition.index >= 0).sort((a, b) => a.index - b.index);
}

function translatedSections(value: string): Map<BilingualPromptSection["key"], string> {
  const markers = fieldMarkers(value);
  const sections = new Map<BilingualPromptSection["key"], string>();
  if (!markers.length) {
    if (value.trim()) sections.set("reference", value.trim());
    return sections;
  }
  const lead = value.slice(0, markers[0].index).trim();
  if (lead) sections.set("reference", lead);
  markers.forEach((marker, index) => {
    const start = marker.index + marker.field.length + 1;
    const end = markers[index + 1]?.index ?? value.length;
    sections.set(marker.key, value.slice(start, end).trim());
  });
  return sections;
}

export function buildBilingualPromptSections(prompt: string, translation = ""): BilingualPromptSection[] {
  const markers = fieldMarkers(prompt);
  const korean = translatedSections(translation);
  if (!markers.length) {
    return prompt.trim() ? [{
      key: "reference",
      label: "프롬프트",
      field: null,
      english: prompt.trim(),
      korean: korean.get("reference") || "",
      start: 0,
      end: prompt.length,
    }] : [];
  }
  const sections: BilingualPromptSection[] = [];
  const lead = prompt.slice(0, markers[0].index).trim();
  if (lead) sections.push({
    key: "reference",
    label: "참조 기준",
    field: null,
    english: lead,
    korean: korean.get("reference") || "",
    start: 0,
    end: markers[0].index,
  });
  markers.forEach((marker, index) => {
    const end = markers[index + 1]?.index ?? prompt.length;
    sections.push({
      key: marker.key,
      label: marker.label,
      field: marker.field,
      english: prompt.slice(marker.index, end).trim(),
      korean: korean.get(marker.key) || "",
      start: marker.index,
      end,
    });
  });
  return sections;
}

type SentencePart = { text: string; index: number };

function fallbackSentences(value: string): SentencePart[] {
  const matches = value.matchAll(/[^.!?。！？]+(?:[.!?。！？]+[”’"']?)?|[.!?。！？]+/g);
  return Array.from(matches, (match) => {
    const raw = match[0];
    const leading = raw.length - raw.trimStart().length;
    return { text: raw.trim(), index: (match.index ?? 0) + leading };
  }).filter((part) => part.text);
}

export function splitPromptSentences(value: string, locale: "en" | "ko" = "en"): SentencePart[] {
  const Segmenter = (Intl as typeof Intl & {
    Segmenter?: new (locale: string, options: { granularity: "sentence" }) => {
      segment: (input: string) => Iterable<{ segment: string; index: number }>;
    };
  }).Segmenter;
  const lines = Array.from(value.matchAll(/[^\n]+/g));
  return lines.flatMap((line) => {
    const lineStart = line.index ?? 0;
    if (!Segmenter) return fallbackSentences(line[0]).map((part) => ({ ...part, index: lineStart + part.index }));
    return Array.from(new Segmenter(locale, { granularity: "sentence" }).segment(line[0]), (part) => {
      const leading = part.segment.length - part.segment.trimStart().length;
      return { text: part.segment.trim(), index: lineStart + part.index + leading };
    }).filter((part) => part.text);
  });
}

export function pairBilingualPromptSentences(english: string, korean: string): BilingualSentencePair[] {
  const englishSentences = splitPromptSentences(english, "en");
  const koreanSentences = splitPromptSentences(korean.replace(/<Image (\d+)>/gi, "<Picture $1>"), "ko");
  if (!englishSentences.length) return [];
  if (!koreanSentences.length) {
    return englishSentences.map((sentence) => ({ ...sentence, english: sentence.text, korean: "" }));
  }

  return englishSentences.map((sentence, index) => {
    const start = Math.floor(index * koreanSentences.length / englishSentences.length);
    let end = Math.floor((index + 1) * koreanSentences.length / englishSentences.length);
    if (end <= start) end = start + 1;
    return {
      english: sentence.text,
      korean: koreanSentences.slice(start, Math.min(end, koreanSentences.length)).map((part) => part.text).join(" "),
      index: sentence.index,
    };
  });
}
